import React from 'react';
import { Card, Row, Col, Statistic, Table, Typography, Spin, Alert, Tag, Divider, Button, message, Tooltip } from 'antd';
import { 
  ExclamationCircleOutlined, 
  ClockCircleOutlined, 
  CheckCircleOutlined,
  ReloadOutlined,
  FileTextOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { useGetDailyReportQuery } from '../../redux/api/dealsApi';

const { Title, Text } = Typography;

// Zendesk Sell base URL for deals
const ZENDESK_DEAL_URL = 'https://app.futuresimple.com/deals';

/**
 * Daily Report Page
 * Displays key metrics for deals and tasks - designed to be screenshot-friendly
 */
const DailyReportPage = () => {
  const { data, isLoading, error, refetch, isFetching } = useGetDailyReportQuery();

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('cs-CZ', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Format datetime for display
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('cs-CZ', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Copy overdue tasks deal links to clipboard
  const copyOverdueTasksLinks = () => {
    const overdueTasks = tasks?.overdue || [];
    
    // Filter tasks linked to deals and get unique deals
    const dealTasks = overdueTasks.filter(task => task.taskable_type === 'Deal' && task.taskable_id);
    
    if (dealTasks.length === 0) {
      message.warning('No deals with overdue tasks to copy');
      return;
    }

    // Group by deal to avoid duplicates
    const uniqueDeals = new Map();
    dealTasks.forEach(task => {
      if (!uniqueDeals.has(task.taskable_id)) {
        uniqueDeals.set(task.taskable_id, {
          name: task.taskable_name || `Deal #${task.taskable_id}`,
          id: task.taskable_id
        });
      }
    });

    // Format as clickable links for Teams/chat
    const lines = Array.from(uniqueDeals.values()).map(deal => 
      `${deal.name}: ${ZENDESK_DEAL_URL}/${deal.id}`
    );
    
    const textToCopy = `🚨 Deals with Overdue Tasks (${uniqueDeals.size}):\n${lines.join('\n')}`;
    
    navigator.clipboard.writeText(textToCopy).then(() => {
      message.success(`Copied ${uniqueDeals.size} deal links to clipboard`);
    }).catch(() => {
      message.error('Failed to copy to clipboard');
    });
  };

  // Copy deals without tasks links to clipboard
  const copyDealsWithoutTasksLinks = () => {
    const dealsWithoutTasks = deals?.without_tasks || [];
    
    if (dealsWithoutTasks.length === 0) {
      message.warning('No deals without tasks to copy');
      return;
    }

    // Format as clickable links for Teams/chat
    const lines = dealsWithoutTasks.map(deal => 
      `${deal.name}: ${ZENDESK_DEAL_URL}/${deal.id}`
    );
    
    const textToCopy = `📋 Deals Without Tasks (${dealsWithoutTasks.length}):\n${lines.join('\n')}`;
    
    navigator.clipboard.writeText(textToCopy).then(() => {
      message.success(`Copied ${dealsWithoutTasks.length} deal links to clipboard`);
    }).catch(() => {
      message.error('Failed to copy to clipboard');
    });
  };

  // Columns for deals without tasks table
  const dealsColumns = [
    {
      title: 'Deal',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: 120,
    },
    {
      title: 'Stage',
      dataIndex: 'stage_name',
      key: 'stage_name',
      width: 150,
      ellipsis: true,
    },
  ];

  // Columns for overdue tasks table
  const overdueTasksColumns = [
    {
      title: 'Task',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: 120,
    },
    {
      title: 'Due Date',
      dataIndex: 'date',
      key: 'date',
      width: 100,
      render: (date) => (
        <Text type="danger">{formatDate(date)}</Text>
      ),
    },
    {
      title: 'Linked To',
      dataIndex: 'taskable_name',
      key: 'taskable_name',
      width: 180,
      ellipsis: true,
    },
  ];

  // Columns for tasks due today
  const tasksTodayColumns = [
    {
      title: 'Task',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: 120,
    },
    {
      title: 'Linked To',
      dataIndex: 'taskable_name',
      key: 'taskable_name',
      width: 180,
      ellipsis: true,
    },
  ];

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
        <Text type="secondary" style={{ marginTop: 16 }}>Loading daily report...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error loading report"
        description={error?.data?.error || 'Failed to load daily report'}
        type="error"
        showIcon
      />
    );
  }

  const { deals, tasks, reportDate } = data || {};

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
            <FileTextOutlined />
            Daily Report
          </Title>
          <Text type="secondary" style={{ fontSize: 16 }}>
            {formatDate(reportDate)}
          </Text>
        </div>
        <Button 
          icon={<ReloadOutlined spin={isFetching} />} 
          onClick={refetch}
          loading={isFetching}
        >
          Refresh
        </Button>
      </div>

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card variant="borderless">
            <Statistic
              title="Active Deals"
              value={deals?.total_active || 0}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card variant="borderless">
            <Statistic
              title="Deals Without Tasks"
              value={deals?.without_tasks_count || 0}
              styles={{ content: { color: deals?.without_tasks_count > 0 ? '#faad14' : '#52c41a' } }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card variant="borderless">
            <Statistic
              title="Overdue Tasks"
              value={tasks?.overdue_count || 0}
              styles={{ content: { color: tasks?.overdue_count > 0 ? '#ff4d4f' : '#52c41a' } }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card variant="borderless">
            <Statistic
              title="Tasks Due Today"
              value={tasks?.due_today_count || 0}
              styles={{ content: { color: tasks?.due_today_count > 0 ? '#1890ff' : '#8c8c8c' } }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Context Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card variant="borderless" size="small">
            <Text type="secondary">
              Total pending tasks: <strong>{tasks?.total_pending || 0}</strong>
            </Text>
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Critical Alerts Section */}
      <Row gutter={[16, 16]}>
        {/* Overdue Tasks */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>
                  <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                  Overdue Tasks
                  <Tag color={tasks?.overdue_count > 0 ? 'red' : 'green'} style={{ marginLeft: 8 }}>
                    {tasks?.overdue_count || 0}
                  </Tag>
                </span>
                {tasks?.overdue?.length > 0 && (
                  <Tooltip title="Copy deal links to clipboard">
                    <Button 
                      type="text" 
                      size="small" 
                      icon={<CopyOutlined />}
                      onClick={copyOverdueTasksLinks}
                    >
                      Copy Links
                    </Button>
                  </Tooltip>
                )}
              </div>
            }
            variant="borderless"
            styles={{ body: { padding: tasks?.overdue?.length > 0 ? 0 : 24 } }}
          >
            {tasks?.overdue?.length > 0 ? (
              <Table
                dataSource={tasks.overdue}
                columns={overdueTasksColumns}
                rowKey="id"
                pagination={false}
                size="small"
                scroll={{ y: 300 }}
              />
            ) : (
              <Alert title="No overdue tasks" type="success" showIcon />
            )}
          </Card>
        </Col>

        {/* Tasks Due Today */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <span>
                <ClockCircleOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                Tasks Due Today
                <Tag color={tasks?.due_today_count > 0 ? 'blue' : 'default'} style={{ marginLeft: 8 }}>
                  {tasks?.due_today_count || 0}
                </Tag>
              </span>
            }
            variant="borderless"
            styles={{ body: { padding: tasks?.due_today?.length > 0 ? 0 : 24 } }}
          >
            {tasks?.due_today?.length > 0 ? (
              <Table
                dataSource={tasks.due_today}
                columns={tasksTodayColumns}
                rowKey="id"
                pagination={false}
                size="small"
                scroll={{ y: 300 }}
              />
            ) : (
              <Alert title="No tasks due today" type="info" showIcon />
            )}
          </Card>
        </Col>

        {/* Deals Without Tasks */}
        <Col span={24}>
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>
                  <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                  Active Deals Without Tasks
                  <Tag color={deals?.without_tasks_count > 0 ? 'orange' : 'green'} style={{ marginLeft: 8 }}>
                    {deals?.without_tasks_count || 0}
                  </Tag>
                </span>
                {deals?.without_tasks?.length > 0 && (
                  <Tooltip title="Copy deal links to clipboard">
                    <Button 
                      type="text" 
                      size="small" 
                      icon={<CopyOutlined />}
                      onClick={copyDealsWithoutTasksLinks}
                    >
                      Copy Links
                    </Button>
                  </Tooltip>
                )}
              </div>
            }
            variant="borderless"
            styles={{ body: { padding: deals?.without_tasks?.length > 0 ? 0 : 24 } }}
          >
            {deals?.without_tasks?.length > 0 ? (
              <Table
                dataSource={deals.without_tasks}
                columns={dealsColumns}
                rowKey="id"
                pagination={{ pageSize: 10, showSizeChanger: false }}
                size="small"
              />
            ) : (
              <Alert title="All active deals have tasks assigned" type="success" showIcon />
            )}
          </Card>
        </Col>
      </Row>

      {/* Footer with generation time */}
      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Report generated at {formatDateTime(data?.generatedAt)}
        </Text>
      </div>
    </div>
  );
};

export default DailyReportPage;

